// Environment checks for the immersive hero (2026-09-24). Built on the shared
// motion kit; adds the two questions only the hero asks.
import { prefersReducedMotion } from '@/lib/motion';

/** A real browser that is allowed to move things. jsdom has no matchMedia,
 *  so tests always get the static page (no GSAP, no SplitText, no WebGL). */
export function canAnimate(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && !prefersReducedMotion();
}

/** three.js 0.180 is WebGL2-only, so a WebGL1-only device gets the CSS
 *  fallback. The probe context is released straight away: browsers cap live
 *  contexts, and a leaked probe would count against the real one. */
export function supportsWebGL2(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const probe = document.createElement('canvas');
    const gl = probe.getContext('webgl2');
    if (!gl) return false;
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return true;
  } catch {
    return false;
  }
}

/** "40 88% 64%" (a shadcn HSL triplet) -> [r, g, b] in 0..1 display space. */
export function hslTripletToRgb(triplet: string): [number, number, number] {
  const m = triplet.trim().match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/);
  if (!m) return [0.96, 0.75, 0.32];
  const h = (Number(m[1]) % 360) / 360;
  const s = Number(m[2]) / 100;
  const l = Number(m[3]) / 100;
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t: number) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return [hue(h + 1 / 3), hue(h), hue(h - 1 / 3)];
}

/** Keeps an aside's em dashes with the word before them, at render time only
 *  (the locale string is untouched): a line must never open on the dash.
 *  At 390 the EN 'Approve before export' card started its second line with
 *  '— before anything', and at 320-360 the ES console heading with '— todo'.
 *  Test queries still match: their normaliser folds the no-break space. */
export function glueDashes(text: string): string {
  return text.replace(/ — /g, '\u00A0— ');
}
